import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { ErrorResponse } from '@/utils/response';
import { axios, CustomAxiosResponse } from '@/utils/axios';
import { Purchase, UpdatePurchaseParams } from './purchase.model';

export interface PurchaseStoreInterface {
  purchase: Purchase | null;
  purchases: Purchase[];
  purchaseStatus: boolean;
  error: ErrorResponse | null;
  createPurchase(precollectionUuid: string): Promise<void>;
  createPurchaseCheckout(param: {
    precollectionUuid: string;
    coupon: string;
  }): Promise<void>;
  fetchPurchasesByUser(): Promise<void>;
  fetchPurchasesByPreCollectionUuid(uuid: string): Promise<void>;
  fetchOnePurchaseByPreCollectionUuid(uuid: string): Promise<void>;
  updateOnePurchaseByPurchaseUuid(params: UpdatePurchaseParams): Promise<void>;
}

@Module({
  namespaced: true,
  dynamic: true,
  name: 'purchase',
  store,
})
class PurchaseStore extends VuexModule implements PurchaseStoreInterface {
  purchase: Purchase | null = null;
  purchases: Purchase[] = [];
  purchaseStatus = false;
  error: ErrorResponse | null = null;

  @Mutation
  updatePurchase(params: Purchase) {
    this.purchase = params;
  }

  @Mutation
  updatePurchases(params: Purchase[]) {
    this.purchases = params;
  }

  @Mutation
  updatePurchaseStatus(params: boolean) {
    this.purchaseStatus = params;
  }

  @Mutation
  setError(params: ErrorResponse) {
    this.error = params;
  }

  @Action
  async createPurchase(precollectionUuid: string): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.post(`/purchases`, { precollectionUuid });
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async createPurchaseCheckout(param: {
    precollectionUuid: string;
    coupon: string;
  }): Promise<void> {
    this.context.commit('setError', null);

    try {
      return await axios.post(`/purchases/checkout`, { ...param });
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchPurchasesByUser(): Promise<void> {
    this.context.commit('updatePurchases', []);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(`/purchases/user`);

      this.context.commit('updatePurchases', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchPurchasesByPreCollectionUuid(uuid: string): Promise<void> {
    this.context.commit('updatePurchases', []);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(
        `/pre-collections/${uuid}/purchases`,
      );

      this.context.commit('updatePurchases', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchOnePurchaseByPreCollectionUuid(uuid: string): Promise<void> {
    this.context.commit('updatePurchase', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(
        `/pre-collections/${uuid}/purchase`,
      );

      this.context.commit('updatePurchase', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async updateOnePurchaseByPurchaseUuid(
    params: UpdatePurchaseParams,
  ): Promise<void> {
    this.context.commit('updatePurchase', null);
    this.context.commit('setError', null);

    const { uuid, progress } = params;

    try {
      const { data }: CustomAxiosResponse = await axios.put(
        `/purchases/${uuid}`,
        {
          progress,
        },
      );

      this.context.commit('updatePurchase', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }
}

export default getModule(PurchaseStore);
